<script>
import { set } from '@shell/utils/object';
import { mapGetters } from 'vuex';
import { defineComponent } from 'vue';
import { allHash } from '@shell/utils/promise';
import isEmpty from 'lodash/isEmpty';
import { _CREATE, _EDIT } from '@shell/config/query-params';
import CreateEditView from '@shell/mixins/create-edit-view';
import FormValidation from '@shell/mixins/form-validation';
import CruResource from '@shell/components/CruResource.vue';
import Loading from '@shell/components/Loading.vue';
import { RcSection, SECTION_TYPE } from '@components/RcSection';
import Banner from '@components/Banner/Banner.vue';
import ClusterMembershipEditor from '@shell/components/form/Members/ClusterMembershipEditor.vue';
import { useClusterMembership, saveRoleBindings } from '@shell/composables/useClusterMembership';
import RcLabelsAndAnnotations from '@shell/components/RcLabelsAndAnnotations.vue';
import Basics from '@pkg/imported/components/Basics.vue';
import DayTwoOps from '@pkg/imported/components/DayTwoOps.vue';
import RcACE from '@shell/edit/provisioning.cattle.io.cluster/tabs/networking/RcACE.vue';
import RcEtcdSnapshotConfig from '@shell/edit/provisioning.cattle.io.cluster/tabs/etcd/RcEtcdSnapshotConfig.vue';
import { NORMAN, MANAGEMENT, CAPI, HCI } from '@shell/config/types';
import RcKeyValue from '@shell/components/form/RcKeyValue.vue';
import { Checkbox } from '@components/Form/Checkbox';
import { NAME as HARVESTER_MANAGER } from '@shell/config/harvester-manager-types';
import { HARVESTER as HARVESTER_FEATURE, mapFeature } from '@shell/store/features';
import { HIDE_DESC, mapPref } from '@shell/store/prefs';
import { addObject } from '@shell/utils/array';
import { initSchedulingCustomization } from '@shell/utils/cluster';
import { AGENT_CONFIGURATION_TYPES, SETTING } from '@shell/config/settings';

import NameNsDescription from '@shell/components/form/NameNsDescription';
import genericImportedClusterValidators from '../util/validators';
import RcPrivateRegistry from '@shell/components/form/RcPrivateRegistry.vue';
import { PRIVATE_REGISTRY_CONTEXT } from '@shell/components/form/PrivateRegistry.constants';
import { privateRegistryRequired } from '@shell/utils/validators/private-registry';
import { IMPORTED_CLUSTER_VERSION_MANAGEMENT, OPERATION_ANNOTATIONS } from '@shell/config/labels-annotations';
import cloneDeep from 'lodash/cloneDeep';
import { VERSION_MANAGEMENT_DEFAULT, DAY_2_OPS_DEFAULT } from '@pkg/imported/util/shared.ts';
import RcAgentConfiguration from '@shell/components/form/RcAgentConfiguration.vue';
import { IMPORTED_DAY_2_OPS } from '@shell/config/features';
import { RcContentGroup } from '@components/Layout';

const HARVESTER_HIDE_KEY = 'cm-harvester-import';
const CLUSTER_AGENT_CUSTOMIZATION = 'clusterAgentDeploymentCustomization';
const FLEET_AGENT_CUSTOMIZATION = 'fleetAgentDeploymentCustomization';

/**
 * Whether any object key present in `original` is missing from `current` (recursing into nested objects, but not
 * into arrays, which are always sent whole). A norman PUT merges nested objects, so a removed key is only dropped
 * server side when the request replaces the whole object.
 */
function hasRemovedKeys(original, current) {
  if (!original || typeof original !== 'object' || Array.isArray(original)) {
    return false;
  }

  if (!current || typeof current !== 'object' || Array.isArray(current)) {
    return Object.keys(original).length > 0;
  }

  return Object.keys(original).some((k) => !(k in current) || hasRemovedKeys(original[k], current[k]));
}
const defaultCluster = {
  agentEnvVars:   [],
  labels:         {},
  annotations:    {},
  importedConfig: {}
};

// Rancher only applies the etcd fields that are set, and an imported cluster's etcd config is empty (or zero values) until configured
function hasEtcdConfig(etcd) {
  return !!etcd && !!(etcd.disableSnapshots || etcd.snapshotScheduleCron || etcd.snapshotRetention > 0 || etcd.s3);
}

export default defineComponent({
  name: 'CruImported',

  components: {
    Basics, RcACE, Loading, CruResource, RcKeyValue, NameNsDescription, RcSection, Banner, ClusterMembershipEditor, RcLabelsAndAnnotations, Checkbox, RcAgentConfiguration, RcPrivateRegistry, RcContentGroup, RcEtcdSnapshotConfig, DayTwoOps
  },

  mixins: [CreateEditView, FormValidation],

  props: {

    mode: {
      type:    String,
      default: _CREATE
    },

    // provisioning cluster object
    value: {
      type:    Object,
      default: () => {
        return {};
      }
    }
  },

  async fetch() {
    const store = this.$store;

    if (this.value.id) {
      const liveNormanCluster = await this.value.findNormanCluster();

      this.normanCluster = await store.dispatch(`rancher/clone`, { resource: liveNormanCluster });
      this.config = this.normanCluster.rke2Config || this.normanCluster.k3sConfig;
      this.clusterHasEtcdConfig = hasEtcdConfig(this.config?.etcd);
      this.s3Backup = !!this.config?.etcd?.s3;
      if ( this.normanCluster && isEmpty(this.normanCluster.localClusterAuthEndpoint) ) {
        set(this.normanCluster, 'localClusterAuthEndpoint', { enabled: false });
      }
      if ( this.normanCluster && !this.normanCluster?.agentEnvVars) {
        this.normanCluster.agentEnvVars = [];
      }
      if ( this.normanCluster && !this.normanCluster?.importedConfig) {
        this.normanCluster.importedConfig = {};
      }

      this.getVersions();
    } else {
      this.normanCluster = await store.dispatch('rancher/create', { type: NORMAN.CLUSTER, ...cloneDeep(defaultCluster) }, { root: true });
    }
    this.privateRegistryEnabled = !!this.normanCluster?.importedConfig?.privateRegistryURL;
    if (!this.isRKE1) {
      await this.initVersionManagement();
    }
    if (!this.isLocal) {
      // The rancher agent that runs on the local cluster is embedded in the rancher pods that are run in the local cluster, so this is not needed.
      const sc = await initSchedulingCustomization(this.normanCluster, this.features, this.$store, this.mode);

      this.clusterAgentDefaultPC = sc.clusterAgentDefaultPC;
      this.clusterAgentDefaultPDB = sc.clusterAgentDefaultPDB;
      this.fleetAgentDefaultPC = sc.fleetAgentDefaultPC;
      this.fleetAgentDefaultPDB = sc.fleetAgentDefaultPDB;
      this.schedulingCustomizationFeatureEnabled = sc.schedulingCustomizationFeatureEnabled;
      this.schedulingCustomizationOriginallyEnabled = sc.schedulingCustomizationOriginallyEnabled;
      this.errors = this.errors.concat(sc.errors);
      if (!this.isRKE1) {
        this.agentConfigurationOriginal = cloneDeep({
          [CLUSTER_AGENT_CUSTOMIZATION]: this.normanCluster[CLUSTER_AGENT_CUSTOMIZATION],
          [FLEET_AGENT_CUSTOMIZATION]:   this.normanCluster[FLEET_AGENT_CUSTOMIZATION],
        });
        this.ensureAgentConfiguration();
      }
      await this.initDayTwoOps();
    }
  },

  setup() {
    return useClusterMembership();
  },

  data() {
    return {
      normanCluster:                            { name: '', importedConfig: { privateRegistryURL: null } },
      PRIVATE_REGISTRY_CONTEXT,
      loadingVersions:                          false,
      config:                                   null,
      allVersions:                              [],
      defaultVersion:                           '',
      versionManagementGlobalSetting:           false,
      versionManagementOld:                     VERSION_MANAGEMENT_DEFAULT,
      dayTwoOpsOld:                             DAY_2_OPS_DEFAULT,
      schedulingCustomizationFeatureEnabled:    false,
      schedulingCustomizationOriginallyEnabled: false,
      clusterAgentDefaultPC:                    null,
      clusterAgentDefaultPDB:                   null,
      fleetAgentDefaultPC:                      null,
      fleetAgentDefaultPDB:                     null,
      // When disabling clusterAgentDeploymentCustomization, we need to replace the whole object
      needsReplace:                             false,
      // The agent deployment customizations as loaded, used to work out whether saving an edit needs to replace them
      agentConfigurationOriginal:               {},
      privateRegistryEnabled:                   false,
      privateRegistryAuthDraft:                 null,
      selectedKubernetesVersion:                null,
      showDeprecatedPatchVersions:              false,
      s3Backup:                                 false,
      clusterHasEtcdConfig:                     false,
      etcdConfigValid:                          true,
      dayTwoOpsGlobalSetting:                   false,
      dayTwoOpsFlagEnabled:                     false,
      fvFormRuleSets:                           [{
        path:  'name',
        rules: ['clusterNameRequired', 'clusterNameChars', 'clusterNameStartEnd', 'clusterNameLength'],
      }, {
        path:  'workerConcurrency',
        rules: ['workerConcurrencyRule']
      }, {
        path:  'controlPlaneConcurrency',
        rules: ['controlPlaneConcurrencyRule']
      }, {
        path:  'privateRegistry',
        rules: ['privateRegistryRequired']
      }
      ],
      AGENT_CONFIGURATION_TYPES,
      SECTION_TYPE
    };
  },

  created() {
    this.registerAfterHook(this.saveRoleBindings, 'save-role-bindings');
  },

  computed: {
    ...mapGetters({ t: 'i18n/t', features: 'features/get' }),
    pullSecrets: {
      get() {
        const secrets = this.normanCluster?.importedConfig?.privateRegistryPullSecrets;

        return secrets?.[0] ?? undefined;
      },
      set(val) {
        if (val) {
          this.normanCluster.importedConfig.privateRegistryPullSecrets = [val];
        } else if (this.normanCluster.importedConfig.privateRegistryPullSecrets) {
          delete this.normanCluster.importedConfig.privateRegistryPullSecrets;
        }
      }
    },
    fvExtraRules() {
      return {
        clusterNameRequired:         genericImportedClusterValidators.clusterNameRequired(this),
        clusterNameChars:            genericImportedClusterValidators.clusterNameChars(this),
        clusterNameStartEnd:         genericImportedClusterValidators.clusterNameStartEnd(this),
        clusterNameLength:           genericImportedClusterValidators.clusterNameLength(this),
        workerConcurrencyRule:       genericImportedClusterValidators.workerConcurrency(this),
        controlPlaneConcurrencyRule: genericImportedClusterValidators.controlPlaneConcurrency(this),
        privateRegistryRequired:     privateRegistryRequired(this),
      };
    },
    dayTwoOps: {
      get() {
        if (typeof this.normanCluster?.annotations?.[OPERATION_ANNOTATIONS.ENABLED] !== 'undefined') {
          return this.normanCluster.annotations[OPERATION_ANNOTATIONS.ENABLED] ;
        }

        return DAY_2_OPS_DEFAULT;
      },
      set(newValue) {
        this.normanCluster.annotations[OPERATION_ANNOTATIONS.ENABLED] = newValue;
      }
    },

    upgradeStrategy: {
      get() {
        if ( this.normanCluster?.rke2Config ) {
          return this.normanCluster.rke2Config?.rke2upgradeStrategy;
        }

        return this.normanCluster?.k3sConfig?.k3supgradeStrategy;
      },
      set(newValue) {
        if ( this.normanCluster?.rke2Config ) {
          this.normanCluster.rke2Config.rke2upgradeStrategy = newValue;
        }

        this.normanCluster.k3sConfig.k3supgradeStrategy = newValue;
      }

    },
    versionManagement: {
      get() {
        return this.normanCluster.annotations[IMPORTED_CLUSTER_VERSION_MANAGEMENT];
      },
      set(newValue) {
        this.normanCluster.annotations[IMPORTED_CLUSTER_VERSION_MANAGEMENT] = newValue;
      }

    },

    isEdit() {
      return this.mode === _EDIT;
    },
    isCreate() {
      return this.mode === _CREATE;
    },
    isK3s() {
      return !!this.value.isK3s;
    },
    isRKE1() {
      return !!this.value.isRke1;
    },
    isRke2() {
      // Also check mgmt status provider for local clusters where spec.rkeConfig may not be set
      // (local RKE2 clusters have no spec.rkeConfig so isRke2 is false, but status.provider is 'rke2')
      const mgmtProvider = this.value.mgmt?.status?.provider;

      return !!(this.value.isRke2 || mgmtProvider?.startsWith('rke2'));
    },
    enableNetworkPolicySupported() {
      // https://github.com/rancher/rancher/pull/33070/files
      return !this.isK3s && !this.isRke2;
    },
    isLocal() {
      return !!this.value.isLocal;
    },

    doneRoute() {
      return this.value?.listLocation?.name;
    },

    /**
     * Version management and Day 2 Operations are configured on the cluster's rke2Config/k3sConfig, which only exists
     * once an imported RKE2/K3s cluster has registered. So this also covers import (no config yet) and RKE1 (never has one).
     */
    hasConfig() {
      return !!this.config;
    },
    showVersionManagement() {
      // The local cluster's version management setting can be changed even though an embedded K3s local cluster never gets a config
      return this.hasConfig || this.isLocal;
    },
    /**
     * While version management and Day 2 Operations are hidden because there's no rke2Config/k3sConfig yet (on import,
     * or until the imported cluster has registered), tell users how they'll be set (from the global settings), and that
     * they can be configured once the cluster has been imported. RKE1 clusters never get a config, and the local cluster has
     * already been imported, so skip them.
     */
    configureAfterImportMessage() {
      if (this.hasConfig || this.isRKE1 || this.isLocal) {
        return '';
      }

      const state = (enabled) => this.t(`imported.configureAfterImport.${ enabled ? 'enabled' : 'disabled' }`);
      const versionManagementState = state(this.versionManagementGlobalSetting);

      if (!this.canHaveDayTwoOps) {
        return this.t('imported.configureAfterImport.versionManagement', { versionManagement: versionManagementState });
      }

      // Only say "will be automatically <state>" once when both global settings agree
      if (this.versionManagementGlobalSetting === this.dayTwoOpsGlobalSetting) {
        return this.t('imported.configureAfterImport.versionManagementAndDayTwoOpsSame', { state: versionManagementState });
      }

      return this.t('imported.configureAfterImport.versionManagementAndDayTwoOps', { versionManagement: versionManagementState, dayTwoOps: state(this.dayTwoOpsGlobalSetting) });
    },
    enableInstanceDescription() {
      return this.isLocal || this.isCreate;
    },
    hideDescriptions: mapPref(HIDE_DESC),

    harvesterEnabled: mapFeature(HARVESTER_FEATURE),

    harvesterLocation() {
      return this.isCreate && !this.hideDescriptions.includes(HARVESTER_HIDE_KEY) && this.harvesterEnabled ? {
        name:   `c-cluster-product-resource`,
        params: {
          product:  HARVESTER_MANAGER,
          resource: HCI.CLUSTER,
        }
      } : null;
    },
    canHaveDayTwoOps() {
      return !this.isLocal && this.dayTwoOpsFlagEnabled;
    },
    showDayTwoOps() {
      return this.hasConfig && this.canHaveDayTwoOps;
    },
    /**
     * Whether day 2 ops are enabled for this cluster, either explicitly or via the global default. The etcd config is
     * only applied to the cluster while they are, so it's hidden (and unset on save) otherwise
     */
    dayTwoOpsEnabled() {
      return this.dayTwoOps === 'true' || (this.dayTwoOps === DAY_2_OPS_DEFAULT && this.dayTwoOpsGlobalSetting);
    },
    // Cluster/fleet agent deployment customization (requests/limits, tolerations, affinity and scheduling
    // customization) isn't supported for the local cluster (its agent is embedded in the Rancher pods) or RKE1 clusters
    showAgentConfiguration() {
      return !this.isLocal && !this.isRKE1;
    },
  },

  methods: {
    async saveRoleBindings() {
      return saveRoleBindings(this.membershipUpdate, this.normanCluster.id);
    },

    unsetEtcdConfigIfDayTwoOpsDisabled() {
      if (!this.showDayTwoOps || this.dayTwoOpsEnabled || typeof this.config?.etcd === 'undefined') {
        return;
      }

      delete this.config.etcd;
      this.s3Backup = false;
      // A replace is only needed to remove an etcd config the cluster already has
      if (this.clusterHasEtcdConfig) {
        this.needsReplace = true;
      }
    },
    async actuallySave() {
      this.agentConfigurationCleanup();

      try {
        if (this.isEdit) {
          this.unsetEtcdConfigIfDayTwoOpsDisabled();

          const replace = this.needsReplace || this.agentConfigurationHasRemovals();

          return await this.normanCluster.save({ replace });
        } else {
          await this.normanCluster.save();

          return await this.normanCluster.waitForProvisioning();
        }
      } catch (e) {
        if (!this.isRKE1) {
          this.ensureAgentConfiguration();
        }

        throw e;
      }
    },

    async getVersions() {
      this.loadingVersions = true;
      this.versionOptions = [];

      try {
        const globalSettings = await this.$store.getters['management/all'](MANAGEMENT.SETTING) || [];
        let hash = {};

        if (this.isK3s) {
          hash = { versions: this.$store.dispatch('management/request', { url: '/v1-k3s-release/releases' }) };

          const defaultK3sSetting = globalSettings.find((setting) => setting.id === 'k3s-default-version') || {};

          this.defaultVersion = defaultK3sSetting?.value || defaultK3sSetting?.default;

          // Use the channel if we can not get the version from the settings
          if (!this.defaultVersion) {
            hash.channels = this.$store.dispatch('management/request', { url: '/v1-k3s-release/channels' });
          }
        } else {
          hash = { versions: this.$store.dispatch('management/request', { url: '/v1-rke2-release/releases' }) };

          const defaultRke2Setting = globalSettings.find((setting) => setting.id === 'rke2-default-version') || {};

          this.defaultVersion = defaultRke2Setting?.value || defaultRke2Setting?.default;

          if (!this.defaultVersion) {
            hash.channels = this.$store.dispatch('management/request', { url: '/v1-rke2-release/channels' });
          }
        }
        const res = await allHash(hash);

        this.allVersions = res.versions?.data || [];
        if (!this.defaultVersion) {
          const channels = res.channels?.data || [];

          this.defaultVersion = channels.find((x) => x.id === 'default')?.latest;
        }

        this.loadingVersions = false;
      } catch (err) {
        this.loadingVersions = false;
        const errors = this.errors;

        errors.push(this.t('imported.errors.kubernetesVersions', { e: err.error || err }));
      }
    },

    kubernetesVersionChanged(val) {
      this.selectedKubernetesVersion = val;
      if ( !this.isK3s ) {
        this.normanCluster.rke2Config.kubernetesVersion = val;
      } else {
        this.normanCluster.k3sConfig.kubernetesVersion = val;
      }
    },
    enableLocalClusterAuthEndpoint(neu) {
      this.normanCluster.localClusterAuthEndpoint.enabled = neu;
      if (!!neu) {
        this.normanCluster.localClusterAuthEndpoint.caCerts = '';
        this.normanCluster.localClusterAuthEndpoint.fqdn = '';
      } else {
        delete this.normanCluster.localClusterAuthEndpoint.caCerts;
        delete this.normanCluster.localClusterAuthEndpoint.fqdn;
      }
    },
    async done() {
      if (this.isCreate) {
        return this.$router.replace({
          name:   'c-cluster-product-resource-namespace-id',
          params: {
            resource:  CAPI.RANCHER_CLUSTER,
            namespace: this.value.metadata.namespace,
            id:        this.normanCluster.id,
          },
        });
      } else {
        if ( !this.doneRoute ) {
          return;
        }

        this.$router.replace({
          name:   this.doneRoute,
          params: this.doneParams || { resource: this.value.type }
        });
      }
    },

    hideHarvesterNotice() {
      const neu = this.hideDescriptions.slice();

      addObject(neu, HARVESTER_HIDE_KEY);

      this.hideDescriptions = neu;
    },
    async initVersionManagement() {
      this.versionManagementGlobalSetting = (await this.$store.dispatch('management/find', { type: MANAGEMENT.SETTING, id: SETTING.IMPORTED_CLUSTER_VERSION_MANAGEMENT })).value === 'true' || false;
      if (!this.normanCluster.annotations[IMPORTED_CLUSTER_VERSION_MANAGEMENT]) {
        this.normanCluster.annotations[IMPORTED_CLUSTER_VERSION_MANAGEMENT] = VERSION_MANAGEMENT_DEFAULT;
      }
      this.versionManagementOld = this.normanCluster.annotations[IMPORTED_CLUSTER_VERSION_MANAGEMENT];
    },
    async initDayTwoOps() {
      try {
        this.dayTwoOpsFlagEnabled = (await this.$store.dispatch('management/find', {
          type: MANAGEMENT.FEATURE,
          id:   IMPORTED_DAY_2_OPS
        }))?.enabled || false;
      } catch {
        this.dayTwoOpsFlagEnabled = false;
      }

      this.dayTwoOpsGlobalSetting = this.$store.getters['management/byId'](MANAGEMENT.SETTING, SETTING.IMPORTED_CLUSTER_DAY2_OPS_DEFAULT)?.value === 'true';
      this.dayTwoOpsOld = !this.normanCluster.annotations[OPERATION_ANNOTATIONS.ENABLED] ? DAY_2_OPS_DEFAULT : this.normanCluster.annotations[OPERATION_ANNOTATIONS.ENABLED];
    },
    etcdChanged(neu) {
      // config is either normanCluster.rke2Config or normanCluster.k3sConfig, so this writes to <rke2|k3s>Config.etcd
      this.config.etcd = neu;
    },
    setSchedulingCustomization({ event, agentType }) {
      if (event) {
        switch (agentType) {
        case AGENT_CONFIGURATION_TYPES.CLUSTER:
          this.needsReplace = false;
          set(this.normanCluster, 'clusterAgentDeploymentCustomization.schedulingCustomization', { priorityClass: this.clusterAgentDefaultPC, podDisruptionBudget: this.clusterAgentDefaultPDB });
          break;
        case AGENT_CONFIGURATION_TYPES.FLEET:
          this.needsReplace = false;
          set(this.normanCluster, 'fleetAgentDeploymentCustomization.schedulingCustomization', { priorityClass: this.fleetAgentDefaultPC, podDisruptionBudget: this.fleetAgentDefaultPDB });
          break;
        default:
        }
      } else {
        switch (agentType) {
        case AGENT_CONFIGURATION_TYPES.CLUSTER:
          this.needsReplace = true;
          delete this.normanCluster.clusterAgentDeploymentCustomization.schedulingCustomization; break;
        case AGENT_CONFIGURATION_TYPES.FLEET:
          this.needsReplace = true;
          delete this.normanCluster.fleetAgentDeploymentCustomization.schedulingCustomization; break;
        default:
        }
      }
    },

    /**
     * Ensure we have empty models for the two agent configurations, so the Cluster/Fleet Agent sections always
     * have an object to bind to
     */
    ensureAgentConfiguration() {
      if (!this.normanCluster[CLUSTER_AGENT_CUSTOMIZATION]) {
        this.normanCluster[CLUSTER_AGENT_CUSTOMIZATION] = {};
      }

      if (!this.normanCluster[FLEET_AGENT_CUSTOMIZATION]) {
        this.normanCluster[FLEET_AGENT_CUSTOMIZATION] = {};
      }
    },

    /**
     * Recursively clean an agent configuration object, so we only send values when the user has configured something
     */
    cleanAgentConfiguration(model, key) {
      if (!model || !model[key]) {
        return;
      }

      const v = model[key];

      if (Array.isArray(v) && v.length === 0) {
        delete model[key];
      } else if (v && typeof v === 'object') {
        Object.keys(v).forEach((k) => {
          // prevent cleanup of "namespaceSelector" when an empty object because it represents all namespaces in pod/node affinity
          // https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.25/#podaffinityterm-v1-core
          if (k !== 'namespaceSelector') {
            this.cleanAgentConfiguration(v, k);
          }
        });

        if (Object.keys(v).length === 0) {
          delete model[key];
        }
      }
    },

    /**
     * Clean both agent configuration objects before save
     */
    agentConfigurationCleanup() {
      this.cleanAgentConfiguration(this.normanCluster, CLUSTER_AGENT_CUSTOMIZATION);
      this.cleanAgentConfiguration(this.normanCluster, FLEET_AGENT_CUSTOMIZATION);
    },

    /**
     * Whether the user removed something from either agent configuration that was there when the form loaded
     */
    agentConfigurationHasRemovals() {
      return [CLUSTER_AGENT_CUSTOMIZATION, FLEET_AGENT_CUSTOMIZATION].some((key) => hasRemovedKeys(this.agentConfigurationOriginal?.[key], this.normanCluster[key]));
    },
  }
});
</script>

<template>
  <Loading v-if="$fetchState.pending" />
  <CruResource
    v-else
    :resource="value"
    :mode="mode"
    :can-yaml="false"
    :done-route="doneRoute"
    :errors="fvUnreportedValidationErrors"
    :validation-passed="fvFormIsValid && (!showDayTwoOps || !dayTwoOpsEnabled || etcdConfigValid)"
    :show-toc="true"
    @error="e=>errors=e"
    @finish="save"
  >
    <div>
      <div>
        <Banner
          v-if="harvesterLocation"
          color="info"
          :closable="true"
          class="mb-20"
          @close="hideHarvesterNotice"
        >
          {{ t('cluster.harvester.importNotice') }}
          <router-link :to="harvesterLocation">
            {{ t('product.harvesterManager') }}
          </router-link>
        </Banner>
        <NameNsDescription
          v-if="!isView"
          v-model:value="normanCluster"
          :mode="mode"
          :namespaced="false"
          :nameEditable="!isEdit"
          nameKey="name"
          descriptionKey="description"
          name-label="cluster.name.label"
          name-placeholder="cluster.name.placeholder"
          description-label="cluster.description.label"
          description-placeholder="cluster.description.placeholder"
          :rules="{name: fvGetAndReportPathRules('name')}"
        />
        <Banner
          v-if="configureAfterImportMessage"
          color="info"
          data-testid="configure-after-import-banner"
        >
          {{ configureAfterImportMessage }}
        </Banner>
      </div>
      <RcSection
        v-if="showVersionManagement"
        :title="t('imported.basics.versionManagement.title')"
        mode="with-header"
        :type="SECTION_TYPE.PRIMARY"
        expandable
        :expanded="true"
      >
        <Basics
          :value="normanCluster"
          :mode="mode"
          :config="config"
          :upgrade-strategy="upgradeStrategy"
          :versions="allVersions"
          :default-version="defaultVersion"
          :loading-versions="loadingVersions"
          :is-local="isLocal"
          :version-management-global-setting="versionManagementGlobalSetting"
          :version-management="versionManagement"
          :version-management-old="versionManagementOld"
          :rules="{workerConcurrency: fvGetAndReportPathRules('workerConcurrency'), controlPlaneConcurrency: fvGetAndReportPathRules('controlPlaneConcurrency') }"
          :selected-kubernetes-version="selectedKubernetesVersion"
          :show-deprecated-patches="showDeprecatedPatchVersions"
          @kubernetes-version-changed="kubernetesVersionChanged"
          @show-deprecated-patches-changed="(val)=>showDeprecatedPatchVersions = val"
          @drain-server-nodes-changed="(val)=>upgradeStrategy.drainServerNodes = val"
          @drain-worker-nodes-changed="(val)=>upgradeStrategy.drainWorkerNodes = val"
          @server-concurrency-changed="(val)=>upgradeStrategy.serverConcurrency = val"
          @worker-concurrency-changed="(val)=>upgradeStrategy.workerConcurrency = val"
          @version-management-changed="(val)=>versionManagement=val"
        />
      </RcSection>
      <RcSection
        v-if="showDayTwoOps"
        :title="t('imported.basics.dayTwoOpsEnabled.title')"
        data-testid="day-two-ops-accordion"
        mode="with-header"
        :type="SECTION_TYPE.PRIMARY"
        expandable
        :expanded="true"
      >
        <DayTwoOps
          :value="dayTwoOps"
          :global-setting="dayTwoOpsGlobalSetting"
          :mode="mode"
          :old-value="dayTwoOpsOld"
          @update:value="(val)=>dayTwoOps=val"
        />
        <RcContentGroup
          v-if="dayTwoOpsEnabled"
          data-testid="etcd-config"
        >
          <RcEtcdSnapshotConfig
            :value="config.etcd"
            :mode="mode"
            :s3-backup="s3Backup"
            :namespace="value.metadata.namespace"
            :register-before-hook="registerBeforeHook"
            @update:value="etcdChanged"
            @s3-backup-changed="(val)=>s3Backup = val"
            @validation-changed="(val)=>etcdConfigValid = val"
          />
        </RcContentGroup>
      </RcSection>
      <RcSection
        :title="t('members.memberRoles')"
        mode="with-header"
        :type="SECTION_TYPE.PRIMARY"
        expandable
        :expanded="true"
      >
        <RcContentGroup>
          <Banner
            v-if="isLocal"
            color="warning"
            label-key="imported.memberRoles.localBanner"
            class="m-0"
          />
          <Banner
            v-if="isEdit"
            color="info"
            class="m-0"
          >
            {{ t('cluster.memberRoles.removeMessage') }}
          </Banner>
          <ClusterMembershipEditor
            v-if="canManageMembers"
            :mode="mode"
            :parent-id="normanCluster.id ? normanCluster.id : null"
            :pending-update="membershipUpdate"
            @membership-update="onMembershipUpdate"
          />
        </RcContentGroup>
      </RcSection>
      <RcLabelsAndAnnotations
        :mode="mode"
        :value="normanCluster"
        expandable
        :expanded="false"
      />
      <RcSection
        v-if="!isCreate && !isRKE1 && (!isLocal || enableNetworkPolicySupported)"
        :title="t('imported.accordions.networking')"
        data-testid="network-accordion"
        mode="with-header"
        :type="SECTION_TYPE.PRIMARY"
        expandable
        :expanded="false"
      >
        <div
          v-if="enableNetworkPolicySupported"
        >
          <RcContentGroup>
            <Banner
              v-if="!!normanCluster.enableNetworkPolicy"
              color="info"
              label-key="imported.network.banner"
              class="m-0"
            />
            <Checkbox
              v-model:value="normanCluster.enableNetworkPolicy"
              :mode="mode"
              :label="t('cluster.rke2.enableNetworkPolicy.label')"
            />
          </RcContentGroup>
        </div>
        <RcSection
          v-if="!isLocal"
          :title="t('cluster.tabs.ace')"
          mode="with-header"
          :type="SECTION_TYPE.SECONDARY"
          expandable
        >
          <RcACE
            v-model:value="normanCluster.localClusterAuthEndpoint"
            :mode="mode"
            @local-cluster-auth-endpoint-changed="enableLocalClusterAuthEndpoint"
            @ca-certs-changed="(val)=>normanCluster.localClusterAuthEndpoint.caCerts = val"
            @fqdn-changed="(val)=>normanCluster.localClusterAuthEndpoint.fqdn = val"
          />
        </RcSection>
      </RcSection>
      <RcSection
        v-if="!isRKE1"
        :title="t('imported.accordions.advanced')"
        data-testid="advanced-accordion"
        mode="with-header"
        :type="SECTION_TYPE.PRIMARY"
        expandable
        :expanded="false"
      >
        <RcSection
          :title="t('imported.accordions.registries')"
          data-testid="registries-accordion"
          mode="with-header"
          :type="SECTION_TYPE.SECONDARY"
          expandable
          :expanded="true"
        >
          <RcPrivateRegistry
            v-model:value="normanCluster.importedConfig.privateRegistryURL"
            v-model:pull-secret="pullSecrets"
            v-model:enabled="privateRegistryEnabled"
            v-model:auth-secret-draft="privateRegistryAuthDraft"
            :context="PRIVATE_REGISTRY_CONTEXT.IMPORTING"
            :mode="mode"
            :rules="fvGetAndReportPathRules('privateRegistry')"
            :register-before-hook="registerBeforeHook"
            checkbox-test-id="private-registry-enable-checkbox"
            input-test-id="private-registry-url"
          />
        </RcSection>
        <RcSection
          :title="t('imported.agentEnv.header')"
          mode="with-header"
          :type="SECTION_TYPE.SECONDARY"
          expandable
          :expanded="true"
        >
          <RcKeyValue
            v-model:value="normanCluster.agentEnvVars"
            :mode="mode"
            key-name="name"
            :as-map="false"
            :preserve-keys="['valueFrom']"
            :supported="(row) => typeof row.valueFrom === 'undefined'"
            :read-allowed="true"
            :value-can-be-empty="true"
            :key-label="t('cluster.agentEnvVars.keyLabel')"
            :parse-lines-from-file="true"
          />
        </RcSection>
        <RcSection
          v-if="showAgentConfiguration && normanCluster.clusterAgentDeploymentCustomization"
          :title="t('cluster.agentConfig.tabs.cluster')"
          data-testid="cluster-agent-config-accordion"
          mode="with-header"
          :type="SECTION_TYPE.SECONDARY"
          expandable
          :expanded="true"
        >
          <RcAgentConfiguration
            v-model:value="normanCluster.clusterAgentDeploymentCustomization"
            data-testid="imported-cluster-agent-config"
            :type="AGENT_CONFIGURATION_TYPES.CLUSTER"
            :mode="mode"
            :show-tolerations="false"
            :show-affinity="false"
            :scheduling-customization-feature-enabled="schedulingCustomizationFeatureEnabled"
            :scheduling-customization-originally-enabled="schedulingCustomizationOriginallyEnabled"
            :default-p-c="clusterAgentDefaultPC"
            :default-p-d-b="clusterAgentDefaultPDB"
            @scheduling-customization-changed="setSchedulingCustomization"
          />
        </RcSection>
        <RcSection
          v-if="showAgentConfiguration && normanCluster.fleetAgentDeploymentCustomization"
          :title="t('cluster.agentConfig.tabs.fleet')"
          data-testid="fleet-agent-config-accordion"
          mode="with-header"
          :type="SECTION_TYPE.SECONDARY"
          expandable
          :expanded="true"
        >
          <RcAgentConfiguration
            v-model:value="normanCluster.fleetAgentDeploymentCustomization"
            data-testid="imported-fleet-agent-config"
            :type="AGENT_CONFIGURATION_TYPES.FLEET"
            :mode="mode"
            :show-tolerations="false"
            :show-affinity="false"
            :scheduling-customization-feature-enabled="schedulingCustomizationFeatureEnabled"
            :scheduling-customization-originally-enabled="schedulingCustomizationOriginallyEnabled"
            :default-p-c="fleetAgentDefaultPC"
            :default-p-d-b="fleetAgentDefaultPDB"
            @scheduling-customization-changed="setSchedulingCustomization"
          />
        </RcSection>
      </RcSection>
    </div>
  </CruResource>
</template>
